<script lang="ts">
  import { onMount } from 'svelte';
  import { getScoreHistory, type ScoreHistoryEntry, type TimelineEvent } from './api';
  import { colorScheme, scoreColorFn, getScoreColors } from './stores';

  interface Props { client: string; }
  let { client }: Props = $props();

  let history = $state<ScoreHistoryEntry[]>([]);
  let events = $state<TimelineEvent[]>([]);
  let loading = $state(true);
  let canvasEl: HTMLCanvasElement | undefined = $state();
  let hoverEntry = $state<ScoreHistoryEntry | null>(null);
  let hoverEvent = $state<TimelineEvent | null>(null);
  let prevClient = '';

  async function load() {
    loading = true;
    try {
      const res = await getScoreHistory(client);
      history = res.history || [];
      events = res.events || [];
    } catch { history = []; events = []; }
    loading = false;
  }

  onMount(() => { load(); });
  $effect(() => { if (client && client !== prevClient) { prevClient = client; load(); } });
  $effect(() => { if (!loading && canvasEl && history.length > 0) { $colorScheme; draw(); } });

  function scoreColor(s: number): string {
    return scoreColorFn($colorScheme)(s);
  }
  function getMeetingColor(): string {
    return getScoreColors($colorScheme).meeting;
  }

  // No band label here on purpose. It used to print one next to the score, named
  // after pipeline stages ('Near Close', 'Active Deal', 'Engaged'), and the client
  // header renders the REAL stage a few pixels away — so a parked account read
  // "NON-CLIENTS … NEAR CLOSE" and looked broken. The stage belongs to the header;
  // this panel shows the score. `scoreColor` still tints the number by the same
  // 75/50/25 bands, so the band is not lost, and the number itself is exact.

  function fmtDate(d: string): string {
    return new Date(d.includes('T') ? d : d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  // Layout
  const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const PAD = { t: 16, r: 16, b: 28, l: 32 };

  interface Point { x: number; y: number; entry: ScoreHistoryEntry; }
  interface EvtPoint { x: number; evt: TimelineEvent; }

  function computePoints(w: number, h: number): { pts: Point[]; evts: EvtPoint[] } {
    const plotW = w - PAD.l - PAD.r;
    const plotH = h - PAD.t - PAD.b;

    // Equal spacing: use index position, not timestamp
    const yFor = (s: number) => PAD.t + plotH - (Math.min(s, 100) / 100) * plotH;

    const ptCount = history.length;
    const pts = history.map((h, i) => ({
      x: PAD.l + (ptCount > 1 ? (i / (ptCount - 1)) * plotW : plotW / 2),
      y: yFor(h.score),
      entry: h,
    }));

    // Place event markers at equal spacing based on sorted position among all dates
    const allDates = [...new Set([...history.map(h => h.date), ...events.map(e => e.date)])].sort();
    const evts = events.map(e => {
      const idx = allDates.indexOf(e.date);
      return {
        x: PAD.l + (allDates.length > 1 ? (idx / (allDates.length - 1)) * plotW : plotW / 2),
        evt: e,
      };
    });

    return { pts, evts };
  }

  function draw(hoverPtIdx: number | null = null, hoverEvIdx: number | null = null) {
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    canvasEl.width = w * DPR;
    canvasEl.height = h * DPR;
    const ctx = canvasEl.getContext('2d')!;
    ctx.scale(DPR, DPR);

    const plotH = h - PAD.t - PAD.b;
    const { pts, evts } = computePoints(w, h);

    // Read theme colors from CSS
    const cs = getComputedStyle(canvasEl);
    const gridColor = cs.getPropertyValue('--border').trim() || '#e5e7eb';
    const labelColor = cs.getPropertyValue('--text-dim').trim() || '#9ca3af';
    const surfaceColor = cs.getPropertyValue('--bg-surface').trim() || '#fff';

    // Clear
    ctx.clearRect(0, 0, w, h);

    // Y grid
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'right';
    for (const tick of [0, 25, 50, 75, 100]) {
      const y = PAD.t + plotH - (tick / 100) * plotH;
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(PAD.l, y); ctx.lineTo(w - PAD.r, y); ctx.stroke();
      ctx.fillStyle = labelColor;
      ctx.fillText(String(tick), PAD.l - 6, y + 3);
    }

    // X labels (spread evenly, max 6)
    ctx.textAlign = 'center';
    const step = Math.max(1, Math.ceil(pts.length / 6));
    for (let i = 0; i < pts.length; i += step) {
      ctx.fillStyle = labelColor;
      ctx.fillText(fmtDate(pts[i].entry.date), pts[i].x, h - 6);
    }
    if (pts.length > 1 && (pts.length - 1) % step !== 0) {
      ctx.fillText(fmtDate(pts[pts.length - 1].entry.date), pts[pts.length - 1].x, h - 6);
    }

    // Meeting dashed lines
    for (let i = 0; i < evts.length; i++) {
      const e = evts[i];
      ctx.save();
      ctx.strokeStyle = getMeetingColor();
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.globalAlpha = hoverEvIdx === i ? 0.5 : 0.2;
      ctx.beginPath(); ctx.moveTo(e.x, PAD.t); ctx.lineTo(e.x, PAD.t + plotH); ctx.stroke();
      ctx.restore();
      // Diamond
      const dy = PAD.t + plotH + 10;
      ctx.fillStyle = getMeetingColor();
      ctx.globalAlpha = hoverEvIdx === i ? 1 : 0.6;
      ctx.beginPath();
      ctx.moveTo(e.x, dy - 5); ctx.lineTo(e.x + 5, dy); ctx.lineTo(e.x, dy + 5); ctx.lineTo(e.x - 5, dy);
      ctx.closePath(); ctx.fill();
      ctx.globalAlpha = 1;
    }

    if (pts.length < 2) {
      if (pts.length === 1) {
        const p = pts[0];
        const c = scoreColor(p.entry.score);
        // Dashed line at score level
        ctx.save();
        ctx.strokeStyle = c + '30';
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 4]);
        ctx.beginPath(); ctx.moveTo(PAD.l, p.y); ctx.lineTo(w - PAD.r, p.y); ctx.stroke();
        ctx.restore();
        // Glow
        ctx.beginPath(); ctx.arc(p.x, p.y, 12, 0, Math.PI * 2);
        ctx.fillStyle = c + '15'; ctx.fill();
        // White center
        ctx.beginPath(); ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
        ctx.fillStyle = surfaceColor; ctx.fill();
        // Dot
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = c; ctx.fill();
        // Score label
        ctx.font = 'bold 13px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = c;
        ctx.fillText(String(p.entry.score), p.x, p.y - 18);
      }
      return;
    }

    // Gradient fill — segmented by score color
    const baseY = PAD.t + plotH;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(pts.length - 1, i + 2)];
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      const segColor = scoreColor(p2.entry.score);
      const grad = ctx.createLinearGradient(0, Math.min(p1.y, p2.y), 0, baseY);
      grad.addColorStop(0, segColor + '20');
      grad.addColorStop(1, segColor + '03');
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
      ctx.lineTo(p2.x, baseY);
      ctx.lineTo(p1.x, baseY);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
    }

    // Line segments — colored by destination score
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(pts.length - 1, i + 2)];
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      ctx.strokeStyle = scoreColor(p2.entry.score);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
      ctx.stroke();
    }

    // Points
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i];
      const isHover = hoverPtIdx === i;
      const r = isHover ? 6 : 4;
      const c = scoreColor(p.entry.score);
      // Outer ring
      ctx.beginPath(); ctx.arc(p.x, p.y, r + 2, 0, Math.PI * 2);
      ctx.fillStyle = c + '25'; ctx.fill();
      // White center
      ctx.beginPath(); ctx.arc(p.x, p.y, r + 1, 0, Math.PI * 2);
      ctx.fillStyle = '#fff'; ctx.fill();
      // Colored dot
      ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fillStyle = c; ctx.fill();
      // Score label on hover
      if (isHover) {
        ctx.font = 'bold 12px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = scoreColor(p.entry.score);
        ctx.fillText(String(p.entry.score), p.x, p.y - 14);
      }
    }
  }

  function onMouseMove(e: MouseEvent) {
    if (!canvasEl || history.length === 0) return;
    const rect = canvasEl.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const { pts, evts } = computePoints(rect.width, rect.height);

    // Check score points
    let closestPt = -1, closestDist = 20;
    for (let i = 0; i < pts.length; i++) {
      const d = Math.hypot(mx - pts[i].x, my - pts[i].y);
      if (d < closestDist) { closestDist = d; closestPt = i; }
    }

    // Check event diamonds
    let closestEv = -1, closestEvDist = 15;
    const plotH = rect.height - PAD.t - PAD.b;
    for (let i = 0; i < evts.length; i++) {
      const dy = PAD.t + plotH + 10;
      const d = Math.hypot(mx - evts[i].x, my - dy);
      if (d < closestEvDist) { closestEvDist = d; closestEv = i; }
    }

    if (closestPt >= 0) {
      hoverEntry = pts[closestPt].entry;
      hoverEvent = null;
      draw(closestPt, null);
    } else if (closestEv >= 0) {
      hoverEvent = evts[closestEv].evt;
      hoverEntry = null;
      draw(null, closestEv);
    } else {
      hoverEntry = null;
      hoverEvent = null;
      draw(null, null);
    }
  }

  function onMouseLeave() {
    hoverEntry = null;
    hoverEvent = null;
    if (canvasEl && history.length > 0) draw(null, null);
  }
</script>

{#if loading}
  <div class="sc-empty">Loading...</div>
{:else if history.length === 0 && events.length === 0}
  <div class="sc-empty">No score history yet.</div>
{:else}
  {@const latest = history[history.length - 1]}
  {@const display = hoverEntry ?? latest}
  {@const color = scoreColor(display.score)}

  <div class="sc" style="--meeting-color: {getMeetingColor()}">
    <div class="sc-head">
      <span class="sc-num" style="color:{color}">{display.score}</span>
      <span class="sc-num-sub">/100</span>
      {#if hoverEntry && hoverEntry !== latest}
        <span class="sc-hover-date">{fmtDate(hoverEntry.date)}</span>
      {:else if hoverEvent}
        <span class="sc-hover-date">Meeting: {fmtDate(hoverEvent.date)}</span>
      {/if}
    </div>
    <div class="sc-reason">
      {#if hoverEvent}
        {hoverEvent.name.replace(/^\d{4}-\d{2}-\d{2}_/, '').replace(/[-_]/g, ' ')}
      {:else}
        {display.reasoning}
      {/if}
    </div>

    <div class="sc-canvas-wrap">
      <canvas
        bind:this={canvasEl}
        onmousemove={onMouseMove}
        onmouseleave={onMouseLeave}
      ></canvas>
    </div>

    {#if events.length > 0}
      <div class="sc-meetings">
        <span class="sc-m-label">Meetings</span>
        {#each events as evt}
          <span
            class="sc-pill"
            class:sc-pill-active={hoverEvent === evt}
            role="button"
            tabindex="0"
            onmouseenter={() => { hoverEvent = evt; hoverEntry = null; requestAnimationFrame(() => { if (canvasEl) { const r = canvasEl.getBoundingClientRect(); const { pts, evts } = computePoints(r.width, r.height); draw(null, evts.findIndex(e => e.evt === evt)); } }); }}
            onmouseleave={() => { hoverEvent = null; requestAnimationFrame(() => { if (canvasEl) draw(null, null); }); }}
          >{fmtDate(evt.date)}</span>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style>
  .sc {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 20px 24px 16px;
  }
  .sc-head {
    display: flex;
    align-items: baseline;
    gap: 2px;
    margin-bottom: 4px;
  }
  .sc-num {
    font-size: 36px;
    font-weight: 800;
    line-height: 1;
    transition: color 0.15s;
  }
  .sc-num-sub {
    font-size: 14px;
    color: var(--text-dim);
  }
  .sc-hover-date {
    font-size: 12px;
    color: var(--text-dim);
    margin-left: auto;
  }
  .sc-reason {
    font-size: 12px;
    line-height: 1.5;
    color: var(--text-muted);
    margin-bottom: 16px;
    min-height: 36px;
  }
  .sc-canvas-wrap {
    height: 200px;
  }
  .sc-canvas-wrap canvas {
    width: 100%;
    height: 100%;
    cursor: crosshair;
  }
  .sc-meetings {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border);
  }
  .sc-m-label {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-right: 4px;
  }
  .sc-pill {
    font-size: 11px;
    color: var(--meeting-color);
    background: color-mix(in srgb, var(--meeting-color) 6%, transparent);
    border: 1px solid color-mix(in srgb, var(--meeting-color) 15%, transparent);
    padding: 2px 10px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.12s;
  }
  .sc-pill:hover, .sc-pill-active {
    background: color-mix(in srgb, var(--meeting-color) 12%, transparent);
    border-color: color-mix(in srgb, var(--meeting-color) 30%, transparent);
  }
  .sc-empty {
    text-align: center;
    padding: 40px;
    color: var(--text-dim);
    font-size: 13px;
  }
</style>
